import React, { useRef, useState, useLayoutEffect } from "react";
import { Tooltip } from "src/assets/imports/antdComponents";
import WaveSelectorModal from "./LegendSelectorModal";

/* -------------------- Types -------------------- */

interface WaveLegendProps {
	waves: Record<string, string>;
	visibleWaves: string[];
	setVisibleWaves: React.Dispatch<React.SetStateAction<string[]>>;
	expanded: boolean;
	setExpanded: React.Dispatch<React.SetStateAction<boolean>>;
	/** Explicit display order. When provided, overrides JS object-key enumeration of `waves`. */
	order?: (string | number)[];
}

/* -------------------- Constants -------------------- */

const MAX_VISIBLE = 3;

/* -------------------- Helpers -------------------- */

// Reorders legend entries by an explicit `order` list. Entries not present in
// `order` keep their original relative position at the end (Array.sort is stable).
const sortByOrder = <T extends { key: string }>(entries: T[], order: (string | number)[]): T[] => {
	const rank = new Map(order.map((key, index) => [String(key), index]));
	return entries.slice().sort((a, b) => {
		const ra = rank.has(a.key) ? (rank.get(a.key) as number) : Number.MAX_SAFE_INTEGER;
		const rb = rank.has(b.key) ? (rank.get(b.key) as number) : Number.MAX_SAFE_INTEGER;
		return ra - rb;
	});
};

/* -------------------- Legend Item with Conditional Tooltip -------------------- */

const LegendItemLabel: React.FC<{
	label: string;
	active: boolean;
	onClick: () => void;
}> = ({ label, active, onClick }) => {
	const labelRef = useRef<HTMLSpanElement>(null);
	const [isTruncated, setIsTruncated] = useState(false);

	// Check if text is truncated
	useLayoutEffect(() => {
		const checkTruncation = () => {
			if (labelRef.current) {
				setIsTruncated(labelRef.current.scrollWidth > labelRef.current.clientWidth);
			}
		};

		checkTruncation();

		const resizeObserver = new ResizeObserver(checkTruncation);
		if (labelRef.current) {
			resizeObserver.observe(labelRef.current);
		}

		return () => resizeObserver.disconnect();
	}, [label]);

	const labelContent = (
		<span
			ref={labelRef}
			onClick={onClick}
			className={`text-[11px] leading-snug truncate ${active ? "text-[--secondary-color]" : "text-gray-400"}`}
		>
			{label}
		</span>
	);

	if (isTruncated) {
		return (
			<Tooltip title={label} placement="top">
				{labelContent}
			</Tooltip>
		);
	}

	return labelContent;
};

/* -------------------- Component -------------------- */

const VerticalLegend: React.FC<WaveLegendProps> = ({ expanded, setExpanded, setVisibleWaves, visibleWaves, waves, order }) => {
	const entries = Object.entries(waves).map(([label, color]) => ({
		key: label,
		label,
		color,
	}));
	const waveEntries = order && order.length > 0 ? sortByOrder(entries, order) : entries;

	const displayedWaves = waveEntries.slice(0, MAX_VISIBLE);

	const toggleWave = (key: string) => {
		setVisibleWaves((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
	};

	if (!visibleWaves) {
		return <></>;
	}

	return (
		<div className="max-w-[240px]">
			<div className="space-y-1">
				{displayedWaves.map((wave) => {
					const active = visibleWaves.includes(wave.key);

					return (
						<div key={wave.key} className="grid grid-cols-[12px_minmax(0,1fr)_auto] items-center gap-2 p-1 rounded-md cursor-pointer hover:bg-gray-50">
							{/* Color Dot */}
							<span
								onClick={() => toggleWave(wave.key)}
								className={`w-3 h-3 rounded-full ${active ? "opacity-100" : "opacity-30"}`}
								style={{ backgroundColor: wave.color }}
							/>

							{/* Label with conditional Tooltip */}
							<LegendItemLabel
								label={wave.label}
								active={active}
								onClick={() => toggleWave(wave.key)}
							/>
						</div>
					);
				})}
				{waveEntries.length > MAX_VISIBLE && (
					<div>
						<button
							onClick={() => setExpanded(true)}
							className="flex items-center gap-1 text-[11px] font-medium text-[--primary-color-2] hover:underline whitespace-nowrap ml-[1.8rem] mt-2">
							<span>Show All ({waveEntries.length})</span>
						</button>
					</div>
				)}
			</div>

			{expanded && <WaveSelectorModal waves={waves} visibleWaves={visibleWaves} setVisibleWaves={setVisibleWaves} onClose={() => setExpanded(false)} />}
		</div>
	);
};

export default VerticalLegend;